import {
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { AppService } from '../../app.service';
import { UtilsService } from '../../shared/utils.service';
import { IndustriesModel } from '../../admin/industries/industries.model';
import { NetworkedEmployeesSelectorComponent } from '../../shared/networked-employees-selector/networked-employees-selector.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountDetailsComponent implements OnInit {
  inProgress: boolean = false;

  canEdit: boolean = false;

  // Whether to show the read-only Hazards & Risks verification row in the Account Info table.
  //
  // Reaching this page only needs account-settings:edit, which says nothing about whether the user
  // works with hazards, so the row also needs the account's plan to include Hazards and the user to
  // hold a permission on the hazards register. Any action counts here, deliberately unlike the
  // Settings page, which requires registers-hazards-risks:edit for the same setting: this row only
  // REPORTS the value, so involvement with the feature is enough to see it, while changing it is an
  // edit. Resolved once here rather than in the template.
  showHazardsVerification: boolean = false;

  industries: IndustriesModel[] = [];

  @ViewChild('fileInput') fileInput: ElementRef;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    public utils: UtilsService,
    private renderer: Renderer2,
    private api: ApiRequestService,
    private rbac: RBACService
  ) {}

  ngOnInit() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_SETTINGS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view this page.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    this.canEdit = true;

    // Accepts any action on the feature, matching how the left navigation decides a feature is part
    // of the user's remit. See the comment on showHazardsVerification for why this is looser than the
    // Settings page gate for the same setting.
    const A = RBACService.RBAC_A;
    this.showHazardsVerification =
      this.app.planCan(AppService.FEATURES.HAZARDS) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, A.A_CREATE),
        RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, A.A_VIEW),
        RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, A.A_EDIT),
        RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS, A.A_DELETE)
      ]);

    // Get Industries
    this.getIndustries();
  }

  private async getIndustries() {
    this.industries = await this.utils.getIndustries();
  }

  // get timezone output from timezone select component
  getOutputTimezone($event) {
    this.app.account.timezone = $event;
  }

  onSubmit(form: NgForm) {
    if (!form.valid) {
      this.utils.showModal(
        'Error',
        'You need to enter valid information in all required fields'
      );
      return;
    }

    if (this.inProgress) {
      return;
    }

    this.inProgress = true;

    this.api
      .makeRequest('put', 'v1/account/' + this.app.account.id, this.app.account)
      .then((response: any) => {
        this.utils.showToast('Your account details were updated.');

        // Apply the account updates
        this.app.account.apply(response.data);

        if (
          typeof this.route.snapshot.queryParams['redirect'] !== 'undefined'
        ) {
          this.router.navigate([
            '/',
            this.route.snapshot.queryParams['redirect']
          ]);
        }

        this.inProgress = false;
      })
      .catch((err: any) => {
        this.inProgress = false;
      });
  }

  /**
   *
   * @param evt
   *
   * Click an ion-button that in turn triggers the file input type to select a file.
   * when a file is selected, it is uploaded.
   *
   * http://pjsdev.blogspot.co.nz/2016/05/ionic-2-beta-6-angular-2-styled-upload.html
   */
  onTriggerFileSelector(evt: Event) {
    if (this.fileInput) {
      const clickEvt: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.fileInput.nativeElement.dispatchEvent(clickEvt);
    }
  }

  // When a file is selected, store it.
  onUploadSelectedFile(evt: any) {
    this.api
      .makeUploadRequest('v1/account/company-logo', evt.target.files[0])
      .then((response) => {
        this.app.account.apply(response);
        this.utils.showToast('Your company logo was updated.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onRemoveCompanyLogo() {
    this.api
      .makeRequest('delete', 'v1/account/company-logo')
      .then((response) => {
        this.app.account.apply(response);
        this.utils.showToast('Your company logo was removed.');
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onSelectMainContact() {
    this.utils.showComponentDialog(
      NetworkedEmployeesSelectorComponent,
      {
        multiple: false,
        deselect: false,
        selected: [this.app.account.main_contact_id]
        // selectedAccountId: this.app.account.id
      },
      {},
      (userId: number) => {
        if (typeof userId == 'undefined') {
          return;
        }

        this.app.account.main_contact_id = userId;
      }
    );
  }

  protected readonly RBACService = RBACService;
}
